import { ArrowRight, ChevronsDownUp, Eye, EyeOff } from "lucide-react";
import {
  Fragment,
  type ReactElement,
  type ReactNode,
  useContext,
  useMemo,
  useState,
} from "react";
import { Link } from "react-router";
import { Badge } from "@/components/ui/Badge";
import { Icon } from "@/components/ui/Icon";
import type { RiskResult } from "@gram/client/models/components/riskresult.js";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover";
import { MoreActions, type Action } from "@/components/ui/MoreActions";
import { CardContextMenu } from "@/components/card-context-menu";
import { useDismissFinding } from "@/pages/security/useDismissFinding";
import { CreateExclusionContext } from "./exclusionContext";
import {
  collapseToMatchWindows,
  getMatchStrings,
  getRiskBadgeLabel,
  highlightMatches,
  maskBlock,
  maskValue,
  matchShownInDescription,
  needsWholeMessageMask,
  resultIsSpanlessSensitive,
  resultsAreSensitive,
  shouldShowRiskRuleId,
  useRowReveal,
} from "./chatHelpers";

/** Marks a stretch of message text collapsed away between (or around) the
 * context windows kept for a long flagged message. */
function Ellipsis(): ReactNode {
  return (
    <span
      className="text-muted-foreground select-none"
      aria-label="elided text"
    >
      {" … "}
    </span>
  );
}

/** A short flagged message rendered inline with the matched span(s) marked in
 * yellow, plus a reveal toggle when the match is sensitive. */
export function HighlightedMessageText({
  text,
  results,
  revealed: controlledRevealed,
}: {
  text: string;
  results: RiskResult[];
  /** When provided, the host owns the reveal state (e.g. a toggle in the
   * message meta strip) and the inline toggle is hidden. */
  revealed?: boolean;
}): ReactNode {
  const matches = useMemo(() => getMatchStrings(results), [results]);
  const sensitive = resultsAreSensitive(results);
  const internal = useRowReveal(sensitive);
  const isControlled = controlledRevealed !== undefined;
  const revealed = controlledRevealed ?? internal.revealed;
  // A finding can match content that was stripped for display (e.g. the
  // <message-context> envelope), so its span isn't in the visible text. Surface
  // each such value explicitly — per-match, so an orphan isn't hidden just
  // because a sibling match happens to appear in the text.
  const orphanMatches = matches.filter((m) => m && !text.includes(m));
  // Long messages bury the flagged span; collapse to a window of context around
  // each match (null when the message is short enough to show whole).
  const snippets = useMemo(
    () => collapseToMatchWindows(text, matches),
    [text, matches],
  );
  const [expanded, setExpanded] = useState(false);
  const collapsed = snippets !== null && !expanded;
  const masked = sensitive && !revealed;
  // A sensitive finding without a span (the LLM analyzer's) leaves nothing to
  // dot out but the message itself, so the whole body masks as one block.
  const maskWhole = needsWholeMessageMask(results);
  // Lets a reviewer act on a flagged (especially just-revealed) secret right
  // where it's read, not just from the turn-level "N risks" popover.
  const actions = useFindingActions(results);
  return (
    <CardContextMenu actions={actions} className="space-y-1">
      {text && (
        <div className="whitespace-pre-wrap">
          {collapsed ? (
            <>
              {snippets[0]!.elidedBefore && <Ellipsis />}
              {snippets.map((snippet, i) => (
                <Fragment key={i}>
                  {i > 0 && <Ellipsis />}
                  {highlightMatches(
                    snippet.text,
                    matches,
                    masked,
                    sensitive,
                    maskWhole,
                  )}
                </Fragment>
              ))}
              {snippets[snippets.length - 1]!.elidedAfter && <Ellipsis />}
            </>
          ) : (
            highlightMatches(text, matches, masked, sensitive, maskWhole)
          )}
        </div>
      )}
      {snippets !== null && (
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs"
          onClick={() => setExpanded((e) => !e)}
        >
          <ChevronsDownUp className="size-3" />
          {expanded ? "Collapse message" : "Show full message"}
        </button>
      )}
      {orphanMatches.length > 0 && (
        <div className="text-muted-foreground space-y-1 text-xs">
          <span>
            Flagged value{orphanMatches.length > 1 ? "s" : ""} (not shown in
            message text):
          </span>
          <div className="flex flex-wrap gap-1">
            {orphanMatches.map((m, i) => (
              <code
                key={i}
                className="bg-destructive/10 text-destructive px-1 py-0.5 font-mono break-all"
              >
                {sensitive && !revealed ? maskValue(m) : m}
              </code>
            ))}
          </div>
        </div>
      )}
      {sensitive && !isControlled && (
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs"
          onClick={() => internal.setRevealed(!internal.revealed)}
        >
          {internal.revealed ? (
            <Eye className="size-3" />
          ) : (
            <EyeOff className="size-3" />
          )}
          {internal.revealed ? "Hide secret" : "Reveal secret"}
        </button>
      )}
    </CardContextMenu>
  );
}

// Unlike security/risk-ui.tsx's MaskedMatch, this toggle is intentionally
// NOT backed by risk.unmaskResult. Its data comes from the chat detail panel's
// useRiskListResults({ chatId }) call, which server-side already returns raw
// match/spans here — the caller already holds chat:read for this exact chat
// (required to load the transcript at all), so the same secret is already
// present in the message content on this page. See the chat:read bypass
// comment on risk.ListRiskResults in server/internal/risk/impl.go.
function MaskedMatchInline({ value }: { value: string }): ReactNode {
  const { revealed, setRevealed } = useRowReveal(true);
  if (!revealed) {
    return (
      <button
        type="button"
        className="text-muted-foreground hover:text-foreground mt-1 inline-flex items-center gap-1 text-xs"
        onClick={() => setRevealed(true)}
      >
        <EyeOff className="h-3 w-3" />
        <span>Click to reveal</span>
      </button>
    );
  }
  return (
    <span className="mt-1 inline-flex items-center gap-1">
      <code className="bg-destructive/10 text-destructive inline-block px-1.5 py-0.5 font-mono text-xs break-all">
        {value}
      </code>
      <button
        type="button"
        className="text-muted-foreground hover:text-foreground"
        onClick={() => setRevealed(false)}
      >
        <Eye className="h-3 w-3" />
      </button>
    </span>
  );
}

// The rationale of a sensitive finding that has no span of its own (the LLM
// analyzer's secret / PII verdicts) may quote the flagged value, so it stays
// dotted out until the viewer asks for it — same gating, and same chat:read
// reasoning, as MaskedMatchInline above.
function MaskedRationale({ text }: { text: string }): ReactNode {
  const { revealed, setRevealed } = useRowReveal(true);
  return (
    <p className="text-muted-foreground mt-1 flex items-start gap-1 text-xs">
      <span className="min-w-0 whitespace-pre-wrap">
        {revealed ? text : maskBlock(text)}
      </span>
      <button
        type="button"
        className="text-muted-foreground hover:text-foreground mt-0.5 shrink-0"
        aria-label={revealed ? "Hide rationale" : "Reveal rationale"}
        onClick={() => setRevealed(!revealed)}
      >
        {revealed ? (
          <Eye className="h-3 w-3" />
        ) : (
          <EyeOff className="h-3 w-3" />
        )}
      </button>
    </p>
  );
}

type FindingSpan = { match: string; field?: string; path?: string };

type TranscriptFindingSpan = FindingSpan;

type TranscriptFinding = {
  id: string;
  label: string;
  rationale?: string;
  /** The rationale may quote a secret or personal datum the finding could not
   * point at otherwise: keep it masked until revealed. */
  rationaleSensitive?: boolean;
  ruleId?: string;
  blockId?: string;
  spans?: TranscriptFindingSpan[];
  tags?: string[];
  count?: number;
  showRuleId?: boolean;
  onMarkFalsePositive?: () => void;
  onSetupExclusionRule?: () => void;
};

// spansOf returns a finding's matched spans: the spans array when the backend
// attributed several (e.g. a custom rule matching a tool's function and its
// arguments on the same call), else the single primary match for legacy rows.
function spansOf(r: RiskResult): FindingSpan[] {
  const fromSpans = (r.spans ?? [])
    .filter((s) => s.match)
    .map((s) => ({ match: s.match, field: s.field, path: s.path }));
  if (fromSpans.length > 0) return fromSpans;
  return r.match ? [{ match: r.match }] : [];
}

// spanFieldLabel renders a span's attribution: the matched message field plus any
// JSON sub-path, so "Write" reads as "tool.function" and a drilled value as
// "tool.args.command". Null when the detector didn't attribute a field.
function spanFieldLabel(span: FindingSpan): string | null {
  if (!span.field) return null;
  return span.path ? `${span.field}.${span.path}` : span.field;
}

function TranscriptFindingsCard({
  findings,
  title = "Risk Findings",
}: {
  findings: TranscriptFinding[];
  title?: string;
}): ReactNode {
  return (
    <div className="space-y-3">
      <div className="text-sm font-semibold">{title}</div>
      <div className="divide-border divide-y">
        {findings.map((finding) => {
          return (
            <div key={finding.id} className="py-2 first:pt-0 last:pb-0">
              <div className="flex items-center gap-2">
                <Badge variant="destructive" className="shrink-0 text-[10px]">
                  {finding.label}
                </Badge>
                {finding.showRuleId && finding.ruleId && (
                  <span className="text-muted-foreground min-w-0 truncate font-mono text-xs">
                    {finding.ruleId}
                  </span>
                )}
                {(finding.count ?? 1) > 1 && (
                  <Badge
                    variant="neutral"
                    className="ml-auto shrink-0 text-[10px]"
                  >
                    ×{finding.count}
                  </Badge>
                )}
                {(finding.onMarkFalsePositive ||
                  finding.onSetupExclusionRule) && (
                  <div className={(finding.count ?? 1) > 1 ? "" : "ml-auto"}>
                    <MoreActions
                      actions={
                        [
                          ...(finding.onMarkFalsePositive
                            ? [
                                {
                                  label: "Mark false positive",
                                  onClick: finding.onMarkFalsePositive,
                                },
                              ]
                            : []),
                          ...(finding.onSetupExclusionRule
                            ? [
                                {
                                  label: "Set up exclusion rule",
                                  onClick: finding.onSetupExclusionRule,
                                },
                              ]
                            : []),
                        ] satisfies Action[]
                      }
                    />
                  </div>
                )}
              </div>
              {finding.rationale &&
                (finding.rationaleSensitive ? (
                  <MaskedRationale text={finding.rationale} />
                ) : (
                  <p className="text-muted-foreground mt-1 text-xs">
                    {finding.rationale}
                  </p>
                ))}
              {/* When a durable tool call block was recorded for this finding's
               * message (any agent — Claude, Cursor, Codex), link to its block
               * page where the viewer can read the full reason and leave 👍/👎
               * feedback. */}
              {finding.blockId && (
                <Link
                  to={`/blocks/${finding.blockId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-primary mt-1.5 inline-flex items-center gap-1 text-xs hover:underline"
                >
                  <ArrowRight className="size-3" />
                  More Info
                </Link>
              )}
              {finding.spans && finding.spans.length > 0 && (
                <div className="mt-1 flex flex-col gap-1">
                  {finding.spans.map((span, i) => {
                    const label = spanFieldLabel(span);
                    return (
                      <div
                        key={`${label ?? ""}-${span.match}-${i}`}
                        className="flex flex-wrap items-center gap-1 text-xs"
                      >
                        {label && (
                          <code className="text-muted-foreground font-mono">
                            {label}:
                          </code>
                        )}
                        <MaskedMatchInline value={span.match} />
                      </div>
                    );
                  })}
                </div>
              )}
              {finding.tags && finding.tags.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {finding.tags.map((tag) => (
                    <Badge key={tag} variant="neutral" className="text-[10px]">
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function riskResultToTranscriptFinding({
  result,
  spans,
  count,
  onMarkFalsePositive,
  onSetupExclusionRule,
}: {
  result: RiskResult;
  spans: FindingSpan[];
  count: number;
  onMarkFalsePositive?: () => void;
  onSetupExclusionRule?: () => void;
}): TranscriptFinding {
  return {
    id: result.id,
    label: getRiskBadgeLabel(result),
    rationale: result.description,
    rationaleSensitive: resultIsSpanlessSensitive(result),
    ruleId: result.ruleId,
    blockId: result.blockId,
    spans: matchShownInDescription(result) ? [] : spans,
    tags: result.tags,
    count,
    showRuleId: shouldShowRiskRuleId(result),
    onMarkFalsePositive,
    onSetupExclusionRule,
  };
}

// Dedupes findings that share a source/rule/spans triple (e.g. the same
// secret flagged by more than one policy pass) into one entry with a count,
// dropping anything already optimistically dismissed. Shared by RiskBadge's
// popover list and useFindingActions' context-menu items so both surfaces
// group findings identically. Keeps every member result (not just the
// first) — dismissing a grouped entry must clear all of them, or the ones
// left behind keep the finding active under the hood.
function groupFindings(
  results: RiskResult[],
  isOptimisticallyDismissed: (id: string) => boolean,
): {
  result: RiskResult;
  spans: FindingSpan[];
  count: number;
  allResults: RiskResult[];
}[] {
  const grouped = new Map<
    string,
    {
      result: RiskResult;
      spans: FindingSpan[];
      count: number;
      allResults: RiskResult[];
    }
  >();
  for (const r of results) {
    if (isOptimisticallyDismissed(r.id)) continue;
    const spans = spansOf(r);
    const key = `${r.source}|${r.ruleId ?? ""}|${spans
      .map((s) => `${s.field ?? ""}:${s.path ?? ""}:${s.match}`)
      .join("|")}`;
    const hit = grouped.get(key);
    if (hit) {
      hit.count++;
      hit.allResults.push(r);
    } else {
      grouped.set(key, { result: r, spans, count: 1, allResults: [r] });
    }
  }
  return [...grouped.values()];
}

/** "Mark false positive" / "Set up exclusion rule" for every distinct
 * finding in `results`, suffixed with the finding's own label once there's
 * more than one so a flat list (a context menu has no room for the popover's
 * per-finding grouping) still reads as which action applies to which. */
function useFindingActions(results: RiskResult[]): Action[] {
  const { dismiss, isOptimisticallyDismissed } = useDismissFinding();
  const openCreateExclusion = useContext(CreateExclusionContext);
  return useMemo(() => {
    const findings = groupFindings(results, isOptimisticallyDismissed);
    return findings.flatMap((f, i): Action[] => {
      const suffix =
        findings.length > 1 ? ` (${getRiskBadgeLabel(f.result)})` : "";
      const actions: Action[] = [
        {
          label: `Mark false positive${suffix}`,
          onClick: () => dismiss(f.allResults),
          separatorBefore: i > 0,
        },
      ];
      // llm_judge findings aren't exclusion-eligible: exclusions don't yet
      // support prompt-based (LLM-judge) policy scoping (AGE-2750).
      if (openCreateExclusion && f.result.ruleId !== "llm_judge") {
        actions.push({
          label: `Set up exclusion rule${suffix}`,
          onClick: () => openCreateExclusion(f.result),
        });
      }
      return actions;
    });
  }, [results, isOptimisticallyDismissed, dismiss, openCreateExclusion]);
}

/** Compact "N risks" badge with a popover listing each unique finding. */
export function RiskBadge({
  results,
  compact = false,
  trigger,
}: {
  results: RiskResult[];
  compact?: boolean;
  /** Custom popover trigger (a single element, for `PopoverTrigger asChild`)
   * — e.g. the "N risks" turn-divider label. Falls back
   * to the default destructive badge. */
  trigger?: ReactElement;
}): ReactNode {
  const { dismiss, isOptimisticallyDismissed } = useDismissFinding();
  const openCreateExclusion = useContext(CreateExclusionContext);
  const findings = useMemo(
    () => groupFindings(results, isOptimisticallyDismissed),
    [results, isOptimisticallyDismissed],
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        {trigger ?? (
          <button
            type="button"
            className="cursor-pointer"
            onClick={(e) => e.stopPropagation()}
          >
            <Badge
              variant="destructive"
              className={compact ? "px-1.5 py-0 text-[10px]" : "text-xs"}
            >
              <Icon
                name="shield-alert"
                className={`mr-1 ${compact ? "size-2.5" : "size-3"}`}
              />
              {findings.length} {findings.length === 1 ? "Risk" : "Risks"}
            </Badge>
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="max-h-[70vh] w-80 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <TranscriptFindingsCard
          findings={findings.map((f) =>
            riskResultToTranscriptFinding({
              ...f,
              onMarkFalsePositive: () => dismiss(f.allResults),
              // llm_judge findings aren't exclusion-eligible: exclusions
              // don't yet support prompt-based (LLM-judge) policy scoping
              // (AGE-2750), matching the same filter the old turn-header
              // "Actions" menu applied before it was merged into this menu.
              onSetupExclusionRule:
                openCreateExclusion && f.result.ruleId !== "llm_judge"
                  ? () => openCreateExclusion(f.result)
                  : undefined,
            }),
          )}
        />
      </PopoverContent>
    </Popover>
  );
}

// Subtle, low-emphasis action styled to sit in the message meta strip alongside
// the cost figure — muted by default, darkens on hover, small leading icon so it
// reads as an action rather than a label.
const META_ACTION_CLASS =
  "text-muted-foreground hover:text-foreground inline-flex shrink-0 cursor-pointer items-center gap-1 text-xs transition-colors";

/** Reveal/hide toggle for a row's masked secret, styled for the meta strip. The
 * host owns the `revealed` state so the bubble's highlighted text stays in sync.
 * Renders nothing when the row has no maskable (sensitive) finding. */
export function RevealSecretButton({
  results,
  revealed,
  onToggle,
}: {
  results: RiskResult[];
  revealed: boolean;
  onToggle: () => void;
}): ReactNode {
  if (!resultsAreSensitive(results)) return null;
  return (
    <button type="button" className={META_ACTION_CLASS} onClick={onToggle}>
      {revealed ? <Eye className="size-3" /> : <EyeOff className="size-3" />}
      {revealed ? "Hide" : "Reveal"}
    </button>
  );
}

/** Thin vertical rule between items in a message meta strip. Rendered next to a
 * sibling (never on its own) so it doesn't dangle. */
export function MetaSeparator(): ReactNode {
  return <span aria-hidden className="bg-border h-3 w-px shrink-0" />;
}
